/**
 * GameSelector Component
 * Displays selectable games as a compact wrapping chip grid
 */

import { getTeamAbbr } from '@parlay-picker/shared/teams';
import { cn } from '@utils/cn';
import { ChevronDown } from 'lucide-react';
import type React from 'react';
import { useCallback, useMemo, useState } from 'react';
import type { GroupedGames } from '../utils';

// =============================================================================
// Helpers
// =============================================================================

/** Compact time for chip display: "7p", "7:30p", "10:30p" */
function formatCompactTime(isoString: string): string {
  const date = new Date(isoString);
  const hours = date.getHours();
  const minutes = date.getMinutes();
  const h12 = hours % 12 || 12;
  const period = hours >= 12 ? 'p' : 'a';
  return minutes === 0
    ? `${h12}${period}`
    : `${h12}:${minutes.toString().padStart(2, '0')}${period}`;
}

// =============================================================================
// Types
// =============================================================================

interface GameSelectorProps {
  groupedGames: GroupedGames[];
  selectedGames: string[];
  onToggleGame: (gameId: string) => void;
  onSelectAll: () => void;
  onDeselectAll: () => void;
  totalGames: number;
}

// =============================================================================
// Component
// =============================================================================

export function GameSelector({
  groupedGames,
  selectedGames,
  onToggleGame,
  onSelectAll,
  onDeselectAll,
  totalGames,
}: GameSelectorProps): React.ReactElement {
  const [isExpanded, setIsExpanded] = useState(
    () => typeof window === 'undefined' || !window.matchMedia('(max-width: 639px)').matches,
  );
  const [collapsedDays, setCollapsedDays] = useState<Set<string>>(new Set());
  const toggleDay = useCallback((label: string) => {
    setCollapsedDays((prev) => {
      const next = new Set(prev);
      if (next.has(label)) next.delete(label);
      else next.add(label);
      return next;
    });
  }, []);

  const allDaysCollapsed = groupedGames.length > 1 && collapsedDays.size === groupedGames.length;
  const toggleAllDays = useCallback(() => {
    setCollapsedDays((prev) =>
      prev.size === groupedGames.length
        ? new Set<string>()
        : new Set(groupedGames.map((g) => g.label)),
    );
  }, [groupedGames]);

  const allSelected = selectedGames.length === totalGames && totalGames > 0;
  const noneSelected = selectedGames.length === 0;

  const flatGames = useMemo(() => groupedGames.flatMap((dg) => dg.games), [groupedGames]);

  return (
    <div className="mb-4" data-testid="game-selector">
      {/* Header row */}
      <div className="flex items-center justify-between gap-2 mb-2">
        <div
          onClick={() => setIsExpanded((prev) => !prev)}
          className="flex items-center gap-1 cursor-pointer min-w-0 flex-1 overflow-hidden"
        >
          <ChevronDown
            className={cn('w-4 h-4 shrink-0 transition-transform', !isExpanded && '-rotate-90')}
          />
          <span className="text-sm text-theme-text-secondary shrink-0">
            {selectedGames.length} of {totalGames} games
          </span>
          {/* Collapsed summary: show selected games as mini badges */}
          {!isExpanded && selectedGames.length > 0 && (
            <div className="flex items-center gap-1 ml-2 min-w-0 overflow-hidden">
              {flatGames
                .filter((g) => selectedGames.includes(g.id || ''))
                .slice(0, 4)
                .map((g) => (
                  <span
                    key={g.id}
                    className="text-[10px] px-1.5 py-0.5 rounded-full whitespace-nowrap shrink-0 bg-theme-primary/30 text-theme-primary"
                  >
                    {getTeamAbbr(g.away_team, g.sport_key)}@{getTeamAbbr(g.home_team, g.sport_key)}
                  </span>
                ))}
              {selectedGames.length > 4 && (
                <span className="text-[10px] text-theme-text-tertiary shrink-0">
                  +{selectedGames.length - 4}
                </span>
              )}
            </div>
          )}
        </div>
        <div className="flex gap-2 shrink-0 relative z-10">
          {isExpanded && groupedGames.length > 1 && (
            <button
              type="button"
              onClick={toggleAllDays}
              className="text-xs px-2 py-1 rounded bg-theme-bg hover:bg-theme-border text-theme-text-secondary transition"
              title={allDaysCollapsed ? 'Expand all days' : 'Collapse all days'}
            >
              {allDaysCollapsed ? '▶ Days' : '▼ Days'}
            </button>
          )}
          <button
            type="button"
            onClick={onSelectAll}
            disabled={allSelected}
            className="text-xs px-2 py-1 rounded bg-theme-bg hover:bg-theme-border text-theme-text-secondary transition disabled:opacity-50"
          >
            All
          </button>
          <button
            type="button"
            onClick={onDeselectAll}
            disabled={noneSelected}
            className="text-xs px-2 py-1 rounded bg-theme-bg hover:bg-theme-border text-theme-text-secondary transition disabled:opacity-50"
          >
            None
          </button>
        </div>
      </div>

      {/* Chip grid */}
      {isExpanded && (
        <div className="space-y-2">
          {groupedGames.map((dayGroup) => {
            const isDayCollapsed = groupedGames.length > 1 && collapsedDays.has(dayGroup.label);
            return (
              <div key={dayGroup.label}>
                {/* Day label — always shown; clickable to collapse when multiple days */}
                <button
                  type="button"
                  onClick={groupedGames.length > 1 ? () => toggleDay(dayGroup.label) : undefined}
                  className={cn(
                    'flex items-center gap-2 mb-1.5 w-full group',
                    groupedGames.length === 1 && 'cursor-default',
                  )}
                >
                  {groupedGames.length > 1 && (
                    <ChevronDown
                      className={cn('w-4 h-4 transition-transform', isDayCollapsed && '-rotate-90')}
                    />
                  )}
                  <span className="text-[10px] font-semibold text-theme-text-tertiary uppercase tracking-wider group-hover:text-theme-text-secondary transition">
                    {dayGroup.label}
                  </span>
                  <div className="flex-1 h-px bg-theme-border/30" />
                </button>
                {/* Game chips — hidden when day is collapsed */}
                {!isDayCollapsed && (
                  <div className="flex flex-wrap gap-1.5">
                    {dayGroup.games.map((game) => {
                      const isSelected = selectedGames.includes(game.id || '');
                      return (
                        <button
                          type="button"
                          key={game.id}
                          onClick={() => onToggleGame(game.id || '')}
                          className={cn(
                            'px-2.5 py-1.5 rounded-md text-xs font-medium transition-all min-h-11 flex items-center gap-1.5',
                            isSelected
                              ? 'bg-theme-primary-fill text-theme-on-primary-fill shadow-sm'
                              : 'bg-theme-surface-hover text-theme-text-secondary hover:bg-theme-surface-active',
                          )}
                        >
                          <span>
                            {getTeamAbbr(game.away_team, game.sport_key)} @{' '}
                            {getTeamAbbr(game.home_team, game.sport_key)}
                          </span>
                          <span
                            className={cn(
                              'opacity-60',
                              isSelected
                                ? 'text-theme-on-primary-fill/70'
                                : 'text-theme-text-tertiary',
                            )}
                          >
                            {formatCompactTime(game.commence_time)}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
